import { Injectable } from '@angular/core';
import { Observable, from, of, catchError, map } from 'rxjs';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { serverTimestamp, Timestamp } from '@angular/fire/firestore';
import {
  FbInvitee,
  JsInvitee,
  CreateInviteInput,
} from '../_interfaces/user.interface';
import { convertTimeStampsFb_Js, getNewId } from '../_utils/utils';
import { FirebaseService } from './firebase.service';

/**
 * InviteService - Handles invite-related write operations and authentication checks
 *
 * NOTE: Invitee list data is now managed by FirestoreStoreService.
 * This service only handles:
 * - Authentication-related invite checks (during login)
 * - Write operations (create, update, delete invites)
 * - Firebase Function calls (send invite emails)
 */
@Injectable({
  providedIn: 'root',
})
export class InviteService {
  constructor(
    private afs: AngularFirestore,
    private firebaseService: FirebaseService
  ) {}

  /**
   * Get invitee by email
   * Used during login to check if user is invited
   */
  getInviteeByEmail(email: string): Observable<JsInvitee | null> {
    return this.afs
      .collection<FbInvitee>('invitees', (ref) =>
        ref.where('email', '==', email.toLowerCase()).limit(1)
      )
      .valueChanges()
      .pipe(
        map((items: FbInvitee[]) => {
          if (items.length === 0) return null;
          return convertTimeStampsFb_Js(items[0]) as JsInvitee;
        }),
        catchError(() => of(null))
      );
  }

  /**
   * Check if an email is invited (returns a promise for easier auth flow integration)
   */
  async checkInviteStatus(email: string): Promise<JsInvitee | null> {
    const snapshot = await this.afs
      .collection<FbInvitee>('invitees', (ref) =>
        ref.where('email', '==', email.toLowerCase()).limit(1)
      )
      .get()
      .toPromise();

    if (!snapshot || snapshot.empty) return null;

    const doc = snapshot.docs[0];
    return convertTimeStampsFb_Js(doc.data()) as JsInvitee;
  }

  /**
   * Create a new invite (admin only)
   * This will also trigger an invite email via Firebase Functions
   */
  async createInvite(
    input: CreateInviteInput,
    adminUid: string,
    adminEmail: string
  ): Promise<JsInvitee> {
    const email = input.email.toLowerCase().trim();

    // Check if invitee already exists
    const existing = await this.checkInviteStatus(email);
    if (existing) {
      throw new Error('This email has already been invited');
    }

    const id = getNewId();
    const now = new Date();

    const invitee: FbInvitee = {
      id,
      email,
      name: input.name || '',
      invitedBy: adminUid,
      invitedByEmail: adminEmail,
      invitedAt: Timestamp.fromDate(now),
      status: 'pending',
      acceptedAt: null,
      lastLoginAt: null,
    };

    await this.afs.collection('invitees').doc(id).set(invitee);

    // Get the current app URL from the browser
    const appUrl = window.location.origin;

    // Trigger invite email via Firebase Function (fire and forget)
    this.sendInviteEmailAsync(email, input.name || '', adminEmail, appUrl).catch((err) =>
      console.error('Failed to send invite email:', err)
    );

    return {
      ...invitee,
      invitedAt: now,
    } as JsInvitee;
  }

  /**
   * Update invitee when they first log in
   * Marks status as 'accepted' and sets acceptedAt
   */
  async markInviteAccepted(inviteeId: string, userUid: string): Promise<void> {
    await this.afs.collection('invitees').doc(inviteeId).update({
      status: 'accepted',
      acceptedAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
      uid: userUid,
    });
  }

  /**
   * Update last login time for an invitee
   */
  async updateLastLogin(inviteeId: string): Promise<void> {
    await this.afs.collection('invitees').doc(inviteeId).update({
      lastLoginAt: serverTimestamp(),
    });
  }

  /**
   * Delete an invitee (admin only)
   */
  async deleteInvitee(inviteeId: string): Promise<void> {
    await this.afs.collection('invitees').doc(inviteeId).delete();
  }

  /**
   * Send invite email via Firebase Function
   */
  private async sendInviteEmailAsync(
    email: string,
    name: string,
    invitedByEmail: string,
    appUrl: string
  ): Promise<any> {
    return this.firebaseService.callableFunction('sendInviteEmail', {
      email,
      name: name || '',
      invitedByEmail,
      appUrl,
    });
  }

  /**
   * Resend invite email for an existing invitee
   */
  resendInviteEmail(email: string, name?: string): Observable<any> {
    // Get current admin email from storage
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const invitedByEmail = user?.email || '';
    // Get the current app URL from the browser
    const appUrl = window.location.origin;

    return from(
      this.firebaseService.callableFunction('sendInviteEmail', {
        email,
        name: name || '',
        invitedByEmail,
        appUrl,
      })
    );
  }
}
